import { Button } from '@creditornot/cb-button';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import Loading from 'core/Loading';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { useCampaignsList } from 'modules/campaigns/hooks/useCampaignsList';
import { CampaignStatus, VenuesValidationError } from 'modules/campaigns/types';
import { getVenueAvailabilityStatus } from 'modules/venue-availability-status/helpers';
import useVenueAvailabilityStatus from 'modules/venue-availability-status/hooks/useVenueAvailabilityStatus';
import { VenueAvailabilityStatus } from 'modules/venue-availability-status/types';
import { VenueSelector, VenueSelectorForm, VenueViewer } from 'modules/venue-selector';

import { SelectedVenueList } from './SelectedVenueList';
import { VenueRowItem } from './VenueRowItem';
import getVenueAlertMessage from './getVenueAlertMessage';
import { CampaignVenueSelectItem } from './types';
import CampaignFormItem from '../CampaignFormItem';

type VenuesFormItemProps = {
  initialValues: VenueSelectorForm;
  startDate: string;
  endDate: string;
  campaignType: CampaignType;
  isDisabled?: boolean;
  venuesValidationErrors?: VenuesValidationError;
  onUpdate(venueIds: string[]): void;
};

const VenueFormItemRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledLoading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const VenuesFormItem = ({
  initialValues,
  startDate,
  endDate,
  campaignType,
  isDisabled,
  venuesValidationErrors,
  onUpdate,
}: VenuesFormItemProps) => {
  const { venueIds } = initialValues; // already selected venue ids
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { entityType, venues, merchantId, timezone, country } = useCampaignModuleContext();
  const merchantVenueIds = venues.map(venue => venue.id);
  const merchantOwnedSelectedVenueIds = venueIds.filter(venueId => merchantVenueIds.includes(venueId));
  const isMerchantLevel = entityType === 'merchant';
  const { campaignsList, isCampaignsLoading } = useCampaignsList({
    merchantId,
    venueIds: venues.map(venue => venue.id),
    status: [CampaignStatus.SCHEDULED, CampaignStatus.RUNNING],
    timezone,
    country,
    isMerchantLevel,
    enabled: !(isMerchantLevel && venues.length === 0),
  });

  const { venuesData: venuesAvailabilityStatus, isLoading: isVenuesAvailabilityStatusLoading } =
    useVenueAvailabilityStatus(merchantVenueIds);

  if (isVenuesAvailabilityStatusLoading || isCampaignsLoading) {
    return (
      <StyledLoading>
        <Loading />
      </StyledLoading>
    );
  }

  const inactiveVenuesIds =
    venuesAvailabilityStatus
      ?.filter(venue => getVenueAvailabilityStatus(venue) === VenueAvailabilityStatus.INACTIVE)
      .map(venue => venue.venue_id) ?? [];

  const venueSelectItems = venues.map(venue => {
    const campaignVenueAlert = getVenueAlertMessage({
      venueId: venue.id,
      campaignOverlapInfo: {
        existingCampaigns: campaignsList,
        newCampaignType: campaignType,
        newCampaignSchedule: { startDate, endDate },
      },
      inactiveVenuesIds,
      timezone,
    });
    return { ...venue, information: campaignVenueAlert };
  });

  const selectedVenueSelectItems = venueSelectItems.filter(venue => venueIds.includes(venue.id));

  return (
    <CampaignFormItem
      data-testid={'venues-form-item'}
      title={<FormattedMessage id="campaigns.form.venues.title" />}
      action={
        <Button variant="lightBlue" size="small" onClick={() => setIsModalOpen(true)} disabled={isDisabled}>
          <FormattedMessage
            id={venueIds.length > 0 ? 'campaigns.form.venues.change-venues' : 'campaigns.form.venues.add-venues'}
          />
        </Button>
      }
      value={
        <VenueFormItemRoot>
          {isDisabled ? (
            <VenueViewer
              rowRenderer={VenueRowItem}
              venueSelectItems={selectedVenueSelectItems}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
            />
          ) : (
            <VenueSelector<CampaignVenueSelectItem>
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              selectedVenueIds={merchantOwnedSelectedVenueIds}
              onUpdate={onUpdate}
              venueSelectItems={venueSelectItems}
              rowRenderer={VenueRowItem}
            />
          )}
          <SelectedVenueList
            venueSelectItems={selectedVenueSelectItems}
            onViewAll={() => setIsModalOpen(true)}
            venuesValidationErrors={venuesValidationErrors}
          />
        </VenueFormItemRoot>
      }
    />
  );
};
